import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

interface UseRealtimeSubscriptionOptions {
  table: string;
  event: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  callback: (payload: any) => void;
  filter?: string;
}

export const useRealtimeSubscription = ({
  table,
  event,
  callback,
  filter,
}: UseRealtimeSubscriptionOptions) => {
  useEffect(() => {
    let channel: RealtimeChannel;

    const setupChannel = () => {
      const channelName = `${table}-changes`;
      
      channel = supabase.channel(channelName);

      const config: any = {
        event,
        schema: 'public',
        table,
      };

      if (filter) {
        config.filter = filter;
      }

      channel
        .on('postgres_changes', config, callback)
        .subscribe((status) => {
          // Only log in development to avoid console errors in production audits
          if (import.meta.env.DEV) {
            console.log(`Realtime subscription status for ${table}:`, status);
          }
          
          // Silently handle connection errors in production
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            // Connection failed - app will continue to work without realtime updates
            if (import.meta.env.DEV) {
              console.warn(`Realtime connection failed for ${table}, continuing without live updates`);
            }
          }
        });
    };

    setupChannel();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [table, event, filter]);
};
